import styled, {css} from 'styled-components';

import {flexColumn, flexColumnCenter, flexCenter, flexCenterVertically} from '~/shared/theme/FlexLayout';
import Button from '~/shared/components/Button';
import {flipOnLTR, media} from '~/shared/theme/utils';
import {screenReaderNotificatorCss} from '~/shared/components/styled';
import ImageWithAlt from '~/shared/components/ImageWithAlt';
import noReordersImageUrl from '~/assets/images/no-reorders.svg';
import {body14Bold, body14Normal, body16Normal} from '~/shared/theme/typography';

const ICON_MARGIN = 16;
const ICON_SIZE = 56;

const CommonText = styled.div`
  ${body16Normal}
  height: 16px;
  color: ${({theme}) => theme.colors.secondary};
  ${flexCenterVertically}
`;

export const ReorderItemsContainer = styled.div.attrs({
  id: 'reorder-dropdown',
})<{isPage?: boolean; showNoReorders?: boolean}>`
  -webkit-overflow-scrolling: touch;
  ${flexColumn}
  height: 100%;
  width: 100%;
  min-height: 400px;
  justify-content: ${({showNoReorders}) => (showNoReorders ? 'center' : 'unset')};
  ${media.minMobile`
    ${({theme, isPage}) => !isPage && `
      position: absolute;
      top: 0;
      height: 100vh;
      min-height: 0;
      max-height: 480px;
      width: ${theme.reorderDropdown.listWidth}px;
    `}
    ${flipOnLTR`
      left: unset;
      right: 0;
    `}
  `}
`;

export const DropdownList = styled.div <{isPage?: boolean}>`
  overflow-x: hidden;
  overflow-y: auto;
  width: 100%;
  padding: 0 8px 8px 8px;
  ${media.minMobile`
    ${({isPage, theme}) => !isPage && css`
      background-color: ${theme.colors.darkBackground};
      border-radius: 12px;
      border: 1px solid ${theme.colors.gray500};
      box-shadow: ${theme.shadows.shadow1};
      min-height: 0;
      max-height: 100%;
      &::-webkit-scrollbar {
        display: none;
      }
    `}
  `}
`;

export const OrderButton = styled(Button)`
  width: 80px;
  height: 32px;
  ${flexCenter}
  cursor: pointer;
  ${body14Normal}
  background-color: ${({theme}) => theme.actionButton.enabled.backgroundColor};
  color: ${({theme}) => theme.actionButton.enabled.color};
  border-radius: 2px;

  &:hover {
    background-color: ${({theme}) => theme.actionButton.enabled.hoverBg};
  }

  ${flipOnLTR`
    margin-right: auto;
  `}
`;

export const ReorderItemWrapper = styled.div<{isPage?: boolean}>`
  ${flexCenterVertically}
  justify-content: space-between;
  width: 100%;
  min-height: 96px;
  height: 96px;
  margin-top: 8px;
  padding: 18px 16px;
  border-radius: 4px;
  background-color: ${({theme}) => theme.colors.surface};
  transition: box-shadow 0.15s ease-in-out;
  ${media.minMobile`
    align-self: center;
    width: 100%;
    max-width: ${({isPage}) => (isPage ? '100%' : '304px')};
  `}
  &:hover {
    ${({isPage}) => !isPage && `
      z-index: 2;
      box-shadow: 0 0 5px 0 rgba(0, 0, 0, 0.3);
    `}
  }
`;

export const LogoAndDetailsWrap = styled.div`
  display: flex;
  flex: 1;
  height: 60px;
  width: 100%;
`;

export const LogoWrap = styled.div`
  ${flipOnLTR`
    margin-left: ${ICON_MARGIN}px;
  `}
`;

export const DetailsWrapper = styled.div`
  ${flexColumn}
  width: 100%;
  overflow: hidden;
`;

export const Logo = styled(ImageWithAlt).attrs(({alt}) => ({
  title: alt,
}))`
  display: block;
  width: ${ICON_SIZE}px;
  height: ${ICON_SIZE}px;
  border-radius: 50%;
  object-fit: cover;
  background-color: ${({theme}) => theme.colors.darkBackground};
  box-shadow: ${({theme}) => theme.shadows.shadow1};
`;

export const RestaurantName = styled(CommonText)`
  font-weight: bold;
  width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: inline-block;
  height: 16px;
  line-height: 1;
  margin-bottom: 4px;
`;

export const PreviewButton = styled(Button)`
  cursor: pointer;
  ${body14Normal}
  color: ${({theme}) => theme.colors.surfacePrimaryAction};
  height: 16px;
  line-height: 16px;
  ${flexCenterVertically}
`;

export const OrderDate = styled(CommonText)`
  color: ${({theme}) => theme.colors.text};
  ${body14Normal}
  line-height: normal;
  margin-bottom: 8px;
`;

export const OverlayGradientBackground = styled.div<{bottom?: boolean; show?: boolean}>`
  position: absolute;
  top: ${({bottom}) => (bottom ? 'unset' : 0)};
  bottom: ${({bottom}) => (bottom ? 0 : 'unset')};
  left: 0;
  width: 100%;
  height: 56px;
  border-radius: 2px;
  background-image: ${({theme, bottom}) =>
    `linear-gradient(to ${bottom ? 'bottom' : 'top'}, rgba(255, 255, 255, 0), ${theme.colors.surface}`});
  z-index: ${({show}) => (show ? 1 : 0)};
  opacity: ${({show}) => (show ? 0.8 : 0)};
  transition: opacity 0.05s ease-in-out;
`;

export const NoReordersWrapper = styled.div<{isPage?: boolean}>`
  ${flexColumnCenter}
  padding: 16px 0;
  ${media.minMobile`
    ${({isPage, theme}) => !isPage && `
      width: ${theme.reorderDropdown.listWidth}px;
      height: 284px;
      background-color: ${theme.colors.surface};
      border-radius: 12px;
      border: 1px solid ${theme.colors.gray500};
      box-shadow: ${theme.shadows.shadow1};
    `}
  `}
`;

export const NoReordersText = styled(CommonText)`
  ${body14Bold}
  margin-top: 16px;
`;

export const NoReordersImage = styled(ImageWithAlt).attrs({
  src: noReordersImageUrl,
})`
  height: 170px;
  width: 189px;
`;

export const ScreenReaderNotificator = styled.label`
  ${screenReaderNotificatorCss}
`;

export const ContentWrapper = styled.div`
  ${flexColumn}
  width: calc(100% - ${ICON_SIZE}px - ${ICON_MARGIN}px);
`;

export const DetailsAndOrderButtonWrapper = styled.div`
  display: flex;
  align-items: flex-end;
`;
